import type { YouTubePhase } from 'features/youtube-streaming/youtube-utils'
import type { MediaProgress } from 'room/common/media-progress'

/**
 * How the console's video monitor shows what the projector shows. The
 * presentation window reports its player (`MediaProgress`: where it is and
 * whether it moves, read together); the monitor is a second player that only
 * ever follows. These are the rules for one look at the two, pure so they are
 * tested without a player.
 *
 * While the projector stands still - paused, waiting for data, at the end -
 * the monitor stands on the same frame. While it plays the monitor keeps
 * within a frame of it: a small drift is taken back by playing a little
 * faster or slower (the monitor is silent, nobody hears it), and only a large
 * one by a jump.
 */

/** One frame at 25 fps: closer than this, both show the same picture. */
export const FRAME_MS = 40
/** Further apart than this while playing, and the monitor jumps. */
export const JUMP_DRIFT_MS = 1000
/** A catch-up aims to take the drift back within about this time. */
export const CATCH_UP_MS = 500
/**
 * The speeds a catch-up goes between, in steps a player takes. Up to twice
 * as fast: a monitor that started a second late is with the projector again
 * within about two seconds.
 */
export const MIN_RATE = 0.5
export const MAX_RATE = 2
export const RATE_STEP = 0.05
/** The furthest a jump lands ahead of the projector, for the jump's own wait. */
export const MAX_JUMP_LEAD_MS = 3000
/**
 * A film this close to its end is left to finish: while the monitor sits at
 * the end and the report still says playing, "play" would show the first
 * frame again until the projector's end reaches the console.
 */
export const END_MARGIN_MS = 1000

export type MonitorCommand =
	| { type: 'play' }
	| { type: 'pause' }
	/** Stand on this frame: the monitor is paused, or about to be. */
	| { type: 'seek'; positionMs: number }
	/** Go to this point and play from it: the projector plays. */
	| { type: 'jump'; positionMs: number }
	| { type: 'rate'; rate: number }

/**
 * Where the projector's player is by now: its report, moved on by the time
 * since while it plays, and never past the end.
 */
export const expectedPositionMs = (
	remote: MediaProgress,
	now: number
): number => {
	const moved = remote.playing ? Math.max(0, now - remote.at) : 0
	const position = remote.positionMs + moved
	return remote.durationMs > 0
		? Math.min(position, remote.durationMs)
		: position
}

/**
 * The speed that takes `driftMs` (monitor minus projector) back within about
 * `CATCH_UP_MS`, in steps of `RATE_STEP`: normal speed when the two are
 * closer than half a step's worth.
 */
export const catchUpRate = (driftMs: number): number => {
	const wanted = 1 - driftMs / CATCH_UP_MS
	const bounded = Math.min(MAX_RATE, Math.max(MIN_RATE, wanted))
	const stepped = Math.round(bounded / RATE_STEP) * RATE_STEP
	return Math.round(stepped * 100) / 100
}

/**
 * How far ahead of the projector the next jump lands: the last jump's lead,
 * corrected by how far off it came down (a jump that landed behind waited
 * that long for its data, and the next will too).
 */
export const nextJumpLeadMs = (leadMs: number, landedDriftMs: number): number =>
	Math.round(Math.min(MAX_JUMP_LEAD_MS, Math.max(0, leadMs - landedDriftMs)))

type Local = {
	positionMs: number
	phase: YouTubePhase
	rate: number
}

type Look = {
	/** The projector's last report; `null` once its player is gone. */
	remote: MediaProgress | null
	/** The monitor's player. */
	local: Local
	/** `Date.now()`, the clock `remote.at` was read from. */
	now: number
	/**
	 * Just after a seek or a jump the monitor's position is not yet where it
	 * was sent: it is not moved again meanwhile, only played or paused.
	 */
	settling: boolean
	/** How far ahead of the projector a jump lands (`nextJumpLeadMs`). */
	jumpLeadMs: number
}

const NOTHING: MonitorCommand[] = []
const PLAY: MonitorCommand = { type: 'play' }
const PAUSE: MonitorCommand = { type: 'pause' }

const isMoving = (phase: YouTubePhase): boolean =>
	phase === 'playing' || phase === 'buffering'

/** The projector stands still: the monitor stands on the same frame. */
const standStill = (
	remote: MediaProgress,
	{ local, settling }: Look
): MonitorCommand[] => {
	const stop = isMoving(local.phase) ? [PAUSE] : NOTHING
	// The projector shows the film's picture, not a frame of it: so does a
	// monitor that has never played, and one that has stays where it is.
	if (remote.kind === 'youtube' && remote.cued) {
		return stop
	}
	const offFrame = Math.abs(local.positionMs - remote.positionMs) > FRAME_MS
	// The projector has left the film's picture - it is getting its data to
	// play - and so does the monitor, which then has its own data by the time
	// the projector plays.
	const onPicture = local.phase === 'cued' || local.phase === 'unstarted'
	return (offFrame || onPicture) && !settling
		? [...stop, { type: 'seek', positionMs: remote.positionMs }]
		: stop
}

/** The projector plays: the monitor plays within a frame of it. */
const playAlong = (
	remote: MediaProgress,
	{ local, now, settling, jumpLeadMs }: Look
): MonitorCommand[] => {
	const expected = expectedPositionMs(remote, now)
	const atTheEnd =
		remote.durationMs > 0 && expected >= remote.durationMs - END_MARGIN_MS
	const driftMs = local.positionMs - expected
	const farOff = Math.abs(driftMs) > JUMP_DRIFT_MS
	const jump: MonitorCommand = {
		type: 'jump',
		positionMs: Math.round(expected + jumpLeadMs),
	}

	switch (local.phase) {
		case 'buffering':
			// On its way: it plays as soon as it has the data.
			return NOTHING
		case 'ended':
			return atTheEnd || settling ? NOTHING : [jump]
		case 'playing': {
			if (settling) {
				return NOTHING
			}
			if (farOff) {
				return [jump]
			}
			const rate = catchUpRate(driftMs)
			return rate === local.rate ? NOTHING : [{ type: 'rate', rate }]
		}
		case 'paused':
		case 'cued':
		case 'unstarted':
			if (atTheEnd) {
				return NOTHING
			}
			return farOff && !settling ? [jump] : [PLAY]
	}
}

/**
 * What the monitor's player should do next, in order: with no report it
 * stops; while the projector stands still it stands on the same frame; while
 * the projector plays it plays along, catching up a small drift by its speed
 * and a large one by a jump.
 */
export const monitorCorrection = (look: Look): MonitorCommand[] => {
	const { remote, local } = look
	if (!remote) {
		return isMoving(local.phase) ? [PAUSE] : NOTHING
	}
	return remote.playing ? playAlong(remote, look) : standStill(remote, look)
}
